import {request} from './apiClient';
import {splitPhoneExtension} from '../utils/phoneExtension';

// Error class to match the Swift error handling.
class EncounterCallError extends Error {
  type: string;
  originalError: any;

  constructor(type: string, message: string, originalError: any = null) {
    super(message);
    this.name = 'EncounterCallError';
    this.type = type;
    this.originalError = originalError;
  }

  static invalidRequest = new EncounterCallError(
    'invalidRequest',
    'Invalid request URL',
  );
  static invalidResponse = new EncounterCallError(
    'invalidResponse',
    'Something went wrong. Please try again.',
  );
  static networkError = (error: any) =>
    new EncounterCallError(
      'networkError',
      'Network error. Please check your connection and try again.',
      error,
    );
  static decodingError = (error: any) =>
    new EncounterCallError(
      'decodingError',
      'Something went wrong. Please try again.',
      error,
    );
  static serverError = (statusCode: number, responseData: any) =>
    new EncounterCallError(
      'serverError',
      'Something went wrong. Please try again.',
      {statusCode, responseData},
    );
}

// Shared POST helper: routes through apiClient (token + base URL handled
// there), keeps the strict status===200 check and EncounterCallError mapping.
async function encounterCallPost(
  path: string,
  payload: Record<string, unknown>,
) {
  try {
    const response = await request(path, {
      method: 'POST',
      parse: 'raw',
      body: JSON.stringify(payload),
    });

    if (response.status !== 200) {
      const responseData = await response.text();
      throw EncounterCallError.serverError(response.status, responseData);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof EncounterCallError) {
      throw error;
    }
    throw EncounterCallError.networkError(error);
  }
}

// Main function to request a phone encounter call - converted from Swift.
const requestCall = async (
  phoneNumber: string,
  language: string,
  dialect: string | null,
  userID: string,
  facilityID: string,
) =>
  encounterCallPost('/initiate-call', {
    phoneNumber: toE164(phoneNumber),
    language,
    dialect,
    userId: userID,
    facilityId: facilityID,
  });

// Normalize a US/E.164-ish phone string to E.164, preserving an optional
// extension as the RFC 3966 ";ext=123" suffix (the dialable number is
// normalized; the extension is carried through verbatim for the SIP layer to
// dial as DTMF). Exported so the web dial-out flow (LiveKit SIP) and the
// single-leg / 3-way calls format numbers identically.
export const toE164 = (phoneNumber: string): string => {
  const {number, extension} = splitPhoneExtension(phoneNumber);
  const hasPlus = number.trim().startsWith('+');
  const cleanNumber = number.replace(/\D/g, '');
  let e164: string;
  if (hasPlus) e164 = `+${cleanNumber}`;
  else if (cleanNumber.length === 10) e164 = `+1${cleanNumber}`;
  else if (cleanNumber.length === 11 && cleanNumber.startsWith('1'))
    e164 = `+${cleanNumber}`;
  else e164 = `+${cleanNumber}`;
  return extension ? `${e164};ext=${extension}` : e164;
};

// Patient-first 3-way concierge encounter call. Opalite calls the patient
// first, confirms readiness in their language, then merges the provider into a
// three-way translated call. POST /api/v1/phone-call/three-way.
const requestThreeWayCall = async (
  patientPhoneNumber: string,
  providerPhoneNumber: string,
  language: string,
  dialect: string | null,
  userID: string,
  facilityID: string,
  callerId?: string,
) =>
  encounterCallPost('/api/v1/phone-call/three-way', {
    patientPhoneNumber: toE164(patientPhoneNumber),
    providerPhoneNumber: toE164(providerPhoneNumber),
    language,
    dialect,
    userId: userID,
    facilityId: facilityID,
    // Company-verified outbound caller ID for both legs; the backend ignores
    // anything that isn't verified for the requester's company.
    ...(callerId ? {callerId} : {}),
  });

// Single-leg phone call. Opalite dials one number (the provider) and the
// agent (interpreter or silent scribe, per `product`) joins; the provider
// merges the patient on their own device. Unified summon endpoint:
// POST /v1/{interpreter,scribe}/sessions with deviceKind: 'phone'.
const requestPhoneCall = async ({
  phoneNumber,
  product = 'interpreter',
  language,
  dialect,
  userId,
  facilityId,
  callerId,
  patientId,
  patientName,
  providerName,
  templateId,
  specialty,
}: {
  phoneNumber: string;
  product?: 'interpreter' | 'scribe';
  language?: string;
  dialect?: string | null;
  userId?: string;
  facilityId?: string;
  callerId?: string;
  patientId?: string;
  patientName?: string;
  providerName?: string;
  templateId?: string;
  specialty?: string;
}) => {
  const endpoint =
    product === 'scribe' ? '/v1/scribe/sessions' : '/v1/interpreter/sessions';
  return encounterCallPost(endpoint, {
    deviceKind: 'phone',
    phoneNumber: toE164(phoneNumber),
    ...(language ? {language} : {}),
    ...(dialect ? {dialect} : {}),
    ...(userId ? {userId} : {}),
    ...(facilityId ? {facilityId} : {}),
    // Company-verified outbound caller ID; the backend ignores anything that
    // isn't verified for the requester's company.
    ...(callerId ? {callerId} : {}),
    context: {
      ...(patientId ? {patientId} : {}),
      ...(patientName ? {patientName} : {}),
      ...(providerName ? {providerName} : {}),
      ...(templateId ? {templateId} : {}),
      ...(specialty ? {specialty} : {}),
    },
  });
};

const endCall = async (callId: string, userId?: string) =>
  encounterCallPost('/end-call', {
    callSid: callId,
    userId,
  });

export {
  requestCall,
  requestPhoneCall,
  requestThreeWayCall,
  endCall,
  EncounterCallError,
};
